import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';

import { Subscription } from 'rxjs';
import { Messages } from 'src/app/constants/messages';
import { ServicecallService } from 'src/app/services/servicecall.service';
import { GeneralService } from 'src/app/services/general.service';
import { EntitiesServiceService } from 'src/app/shared/services/entities-service.service';
import {
  HexagonListModel,
  ModuleHealthModel,
  ModuleHealthPayloadModel,
  ResponseModel,
  StatusModel,
  TopicCountModel
} from 'src/app/model';

@Component({
  selector: 'app-module-health',
  templateUrl: './module-health.component.html',
  styleUrls: ['./module-health.component.css']
})
export class ModuleHealthComponent implements OnInit, OnDestroy {

  @Input()
  set entityData(value: any) {
    const [startTime, endTime] = this._generalService?.getStartEndTimeByDate();
    this._filterPayload = {
      deviceId: value?.device?.deviceId,
      timeRange: {
        from: startTime,
        to: Date.now()
      }
    };
    this._entityPayloadData = {
      from: this._datePipe.transform(this._startTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
      to: this._datePipe.transform(Date.now(), 'yyyy-MM-ddTHH:mm:ssZZ'),
      siteId: this._apiService.getSiteId(),
      entityId: value?._id,
      entityCategory: value?.category
    };
    this._entityDetails = value;
  }

  moduleMetrics: any = [];
  topicCountList: TopicCountModel[];
  overallUtilisation: StatusModel;
  noData: boolean;
  private _entityDetails: any = {};
  private _startTime: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate(),
    0,
    0,
    0,
    999
  );
  private _configData: any = [];

  readonly MESSAGES = Messages;

  // API
  private _filterPayload: ModuleHealthPayloadModel;
  private _entityPayloadData: any;

  // Subscription
  private _moduleMetricsSubscription: Subscription;
  private _getAttributesDataSubscription: Subscription;

  constructor(
    private _apiService: ServicecallService,
    private _generalService: GeneralService,
    private _datePipe: DatePipe,
    private _service: EntitiesServiceService
  ) { }

  ngOnInit(): void {
    if (this._entityDetails?.device?.os === 'windows') {
      this._configData = this._getTopattributes();
      if (this._configData?.length) {
        this._getEntitiesData();
      } else {
        this._getModuleMetrics();
      }
    } else {
      this._getModuleMetricsV2();
    }
  }

  ngOnDestroy(): void {
    this._moduleMetricsSubscription?.unsubscribe();
    this._getAttributesDataSubscription?.unsubscribe();
  }

  /**
   * Gets topattributes
   * @returns
   */
  private _getTopattributes() {
    const CategoryBasedConfig = this._apiService.getConfigurationBasedOnCategory(
      this._entityDetails
    );
    return CategoryBasedConfig ? CategoryBasedConfig?.moduleHealth : [];
  }

  /**
   * Get Attributes Data
   */
  private _getEntitiesData() {
    this._getAttributesDataSubscription = this._service
      .getEntityData(this._entityPayloadData)
      .subscribe({
        next: (result: any) => {
          if (result['status']) {
            if (result['response']?.['data']?.length) {
              if (result['response']?.['data']?.[0]?.attributes?.length) {
                [...this._configData].forEach(
                  (elem: any) => {
                    const filtedStatusAttribute = [...result['response']['data'][0]?.attributes]?.find((item: any) => item?.attributeName === elem?.status);
                    if (filtedStatusAttribute && (Math?.floor(+filtedStatusAttribute?.attributeValue) || (Math?.floor(+filtedStatusAttribute?.attributeValue) === 0))) {
                      const filtedCpuAttribute = [...result['response']['data'][0]?.attributes]?.find((item: any) => item?.attributeName === elem?.cpu);
                      this._setHexagonData({ value: +filtedCpuAttribute?.attributeValue, label: elem?.name, status: Math?.floor(+filtedStatusAttribute?.attributeValue) });
                    } else if (Math?.floor(+filtedStatusAttribute?.attributeValue) !== 0) {
                      this._setHexagonData({ value: 'unknown', label: elem?.name });
                    }
                  });
                this._getModuleMetrics(false);
              } else {
                if (this._configData?.length) {
                  [...this._configData].forEach(
                    (elem: any) => {
                      this._setHexagonData({ value: 'unknown', label: elem?.name });
                    });
                  this._getModuleMetrics(false);
                } else {
                  this._getModuleMetrics();
                }
              }
            } else {
              this._getModuleMetrics();
            }
          } else {
            this._getModuleMetrics();
          }
        },
        error: (error) => {
          this._getModuleMetrics();
        },
      });
  }

  private _setHexagonData(attrData: any) {
    const hexgonDataTemplate = {
      label: attrData?.label,
      unit: "%",
      value: attrData?.value,
      utilisation: this._setUtilisation(attrData?.value, attrData?.status)
    };
    this.moduleMetrics.push(hexgonDataTemplate);
  }

  private _setUtilisation(attrValue: number | string, attrStatus: number = 1) {
    switch (true) {
      case (attrStatus === 0): return {
        type: "unknown",
        title: "Unknown",
        colorCode: "#FE343E",
        backgroundColorCode: "#FBE7E8"
      };
      case (attrValue === 'unknown'): return {
        type: "unknown",
        title: "Unknown",
        colorCode: "#C0C6CB",
        backgroundColorCode: "#EAECEE"
      };
      case (+attrValue > 75): return {
        type: "critical",
        title: "Critical",
        colorCode: "#FE343E",
        backgroundColorCode: "#FBE7E8"
      };
      case ((+attrValue <= 75) && (+attrValue >= 65)): return {
        type: "warning",
        title: "Warning",
        colorCode: "#FFC34B",
        backgroundColorCode: "#FCCC70"
      };
      case (+attrValue < 65): return {
        type: "optimal",
        title: "Optimal",
        colorCode: "#1F9254",
        backgroundColorCode: "#EBF9F1"
      };
      default: return {
        type: "unknown",
        title: "Unknown",
        colorCode: "#C0C6CB",
        backgroundColorCode: "#EAECEE"
      };
    }
  }

  /**
   * Gets module metrics
   */
  private _getModuleMetrics(dummyData: boolean = true) {
    const result: ModuleHealthModel = {
      series: [
        {
          label: "Module 1",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 2",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 3",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 4",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 5",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 6",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 7",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 8",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 9",
          unit: "%",
          value: 0,
          utilisation: {
            type: "warning",
            title: "Warning",
            colorCode: "#b1c309",
            backgroundColorCode: "#FFC55526"
          }
        },
        {
          label: "Module 10",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 11",
          unit: "%",
          value: 99.99999999999999,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 12",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 13",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 14",
          unit: "%",
          value: 99.99999999999999,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        },
        {
          label: "Module 15",
          unit: "%",
          value: 100,
          utilisation: {
            type: "optimal",
            title: "Optimal",
            colorCode: "#1F9254",
            backgroundColorCode: "#EBF9F1"
          }
        }
      ],
      utilisation: {
        type: "warning",
        title: "Warning",
        colorCode: "#b1c309",
        backgroundColorCode: "#FFC55526"
      }
    };
    if (dummyData) {
      this.moduleMetrics = result?.series?.length ? result?.series : [];
      this.overallUtilisation = result?.utilisation
        ? result?.utilisation
        : {} as StatusModel;
    } else {
      this.overallUtilisation = this._getOverallUtilisation();
    }
    this.topicCountList = this._getTopicCountList(this.moduleMetrics);
  }

  /**
   * Gets overall utilisation
   * @returns overall utilisation
   */
  private _getOverallUtilisation(): StatusModel {
    let overallUtilisation: StatusModel;
    const utilisationOrder = ['critical', 'warning', 'optimal', 'unknown'];
    if (this.moduleMetrics?.length) {
      overallUtilisation = utilisationOrder?.reduce(
        (accumulator: StatusModel, utilisation: any) => {
          const currentModule = this.moduleMetrics?.find(module => module?.utilisation?.type === utilisation);
          if (accumulator === null && this._generalService?.isObject(currentModule)) {
            accumulator = <StatusModel>currentModule?.utilisation;
          }
          return accumulator;
        }, null
      );
    }
    return overallUtilisation;
  }

  /**
   * Gets module metrics
   */
  private _getModuleMetricsV2() {
    // Set default
    this.noData = false;
    // API call
    this._moduleMetricsSubscription = this._apiService.getModuleMetrics(this._filterPayload)
      .subscribe({
        next: (result: ResponseModel<ModuleHealthModel>) => {
          if (result?.data?.series?.length) {
            this.moduleMetrics = result?.data?.series?.length ? result?.data?.series : [];
            this.overallUtilisation = result?.data?.utilisation
              ? result?.data?.utilisation
              : {} as StatusModel;
            this.topicCountList = this._getTopicCountList(this.moduleMetrics);
          } else {
            this.noData = true;
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.noData = true;
          this._apiService.toasterService({ type: 'Error', msg: errorResponse?.error?.response?.message });
        }
      });
  }

  /**
   * Gets topic count list
   * @param moduleMetrics 
   * @returns topic count list
   */
  private _getTopicCountList(moduleMetrics: HexagonListModel[]): TopicCountModel[] {
    let topicCountList: TopicCountModel[];
    const utilisationTypes = [...new Set(moduleMetrics.map(module => module?.utilisation?.type))];
    topicCountList = utilisationTypes?.reduce((accumulator: any, value: string) => {
      const moduleMetricsByType = moduleMetrics?.filter((module) => module?.utilisation?.type === value);
      accumulator.push({
        label: moduleMetricsByType?.[0]?.utilisation?.title,
        labelType: "status",
        count: moduleMetricsByType?.length,
        category: this.MESSAGES?.MODULEHEALTH_MODULES_CATEGORY,
        status: moduleMetricsByType?.[0]?.utilisation
      });
      return [...accumulator];
    },
      [
        {
          label: this.MESSAGES?.MODULEHEALTH_TOTALMODULES_LABEL,
          labelType: "label",
          count: moduleMetrics?.length,
          category: this.MESSAGES?.MODULEHEALTH_TOTALMODULES_CATEGORY
        }
      ]
    );
    return topicCountList;
  }
}
